import React, { useState, useEffect, useRef } from "react";
import classes from './PersonalLedger.module.css';
import HeaderNav from '../../Components/Dahboard Header/HeaderNav/HeaderNav';
import { useLocation } from 'react-router-dom';
import { useNavigate } from "react-router-dom";
import html2pdf from 'html2pdf.js';
import Swal from 'sweetalert2';
import {
    BarChart2,
    Filter,
    CalendarClock,
    Mail,
    ArrowLeft,
    Printer,
    Download,
    Settings
} from 'lucide-react';
import Footer from "../../Components/Footer/Footer";
import api from '../../Components/API/Api';

const PersonalLedger = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const printAreaRef = useRef(null);

    // Get customer data from navigation state
    const customerName = location.state?.customerName || "Customer Name";
    const customerId = location.state?.customerId || null;
    const customerDetails = location.state?.customerDetails || null;
    const filteredInvoices = location.state?.filteredInvoices || [];
    const totalSales = location.state?.totalSales || 0;
    const totalPaid = location.state?.totalPaid || 0;
    const totalOutstanding = location.state?.totalOutstanding || 0;
    const totalInvoices = location.state?.totalInvoices || 0;

    // Convert invoice data to ledger entries
    const [ledgerData, setLedgerData] = useState([]);
    
    // Company info state
    const [companyInfo, setCompanyInfo] = useState({
        name: "Brookes Professional Services Limited",
        address: "123 Business Street, Lagos, Nigeria",
        email: "info@yourcompany.com",
        phone: "+234 xxx xxx xxxx"
    });
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const recordsPerPage = 5;

    // Helper functions for currency formatting
    const formatMoney = (value) => {
        const num = Number(value || 0);
        return num.toLocaleString("en-NG", { minimumFractionDigits: 2 });
    };

    const getPaymentStatus = (debtAmount, totalAmount) => {
        const debtNum = parseFloat(debtAmount || 0);
        const totalNum = parseFloat(totalAmount || 0);

        if (debtNum === 0) {
            return "Paid";
        } else if (debtNum === totalNum) {
            return "Owing";
        } else {
            return "Partial";
        }
    };

    // Updated useEffect to handle the data
    useEffect(() => {
        console.log("PersonalLedger received data:", {
            customerName,
            customerId,
            customerDetails,
            filteredInvoices,
            totalSales,
            totalPaid,
            totalOutstanding,
            totalInvoices
        });

        // Transform ledger/invoice data to proper ledger entries
        if (filteredInvoices && filteredInvoices.length > 0) {
            const transformedData = [];

            filteredInvoices.forEach((item, index) => {
                const totalAmount = parseFloat(item.balance || item.total_price || item.amount || 0);
                const debtAmount = parseFloat(item.debit || 0);
                const creditAmount = parseFloat(item.credit || 0);

                // If it's a ledger entry with both debit and credit info
                if (item.debit || item.credit) {
                    transformedData.push({
                        id: item.id || `ledger_${index}`,
                        date: item.created_at?.split("T")[0] || item.transaction_date || new Date().toISOString().split("T")[0],
                        ref: item.invoice_number || item.uuid || item.reference || `REF-${item.id || index}`,
                        description: item.description || 'Transaction',
                        account: item.sales_type === 'cash' ? '1100-Cash' : '1200-Accounts Receivable',
                        debit: item.debit ? formatMoney(item.debit) : '',
                        credit: item.credit ? formatMoney(item.credit) : '',
                        balance: formatMoney(item.balance || 0),
                        type: 'ledger_entry',
                        rawDebit: parseFloat(item.debit || 0),
                        rawCredit: parseFloat(item.credit || 0)
                    });
                } else {
                    // If it's invoice data, create appropriate entries
                    // Sale entry (debit)
                    transformedData.push({
                        id: `${item.id}_sale`,
                        date: item.created_at?.split("T")[0] || item.transaction_date || new Date().toISOString().split("T")[0],
                        ref: item.invoice_number || item.uuid || `INV-${item.id}`,
                        description: `Sales Invoice - ${item.description || 'Product/Service Sale'}`,
                        account: '1200-Accounts Receivable',
                        debit: formatMoney(totalAmount),
                        credit: '',
                        balance: formatMoney(totalAmount),
                        type: 'sale',
                        rawDebit: totalAmount,
                        rawCredit: 0
                    });

                    // Payment entry (credit) - only if partially or fully paid
                    const paidAmount = totalAmount - debtAmount;
                    if (paidAmount > 0) {
                        transformedData.push({
                            id: `${item.id}_payment`,
                            date: item.created_at?.split("T")[0] || item.transaction_date || new Date().toISOString().split("T")[0],
                            ref: item.invoice_number || item.uuid || `PAY-${item.id}`,
                            description: `Payment Received - ${item.description || 'Payment for Invoice'}`,
                            account: '1100-Cash/Bank',
                            debit: '',
                            credit: formatMoney(paidAmount),
                            balance: formatMoney(debtAmount),
                            type: 'payment',
                            rawDebit: 0,
                            rawCredit: paidAmount
                        });
                    }
                }
            });

            // Sort by date
            const sortedData = transformedData.sort((a, b) => new Date(a.date) - new Date(b.date));
            setLedgerData(sortedData);
        } else {
            // No data available
            setLedgerData([]);
        }
    }, [filteredInvoices, customerDetails]);

    // Fetch company info
    useEffect(() => {
        const fetchCompanyInfo = async () => {
            try {
                const res = await api.get('/v1/companies/get-info');
                const data = Array.isArray(res?.data?.data) ? res.data.data[0] : res?.data?.data;
                if (data) {
                    setCompanyInfo({
                        name: data.name || "Brookes Professional Services Limited",
                        address: data.address || "123 Business Street, Lagos, Nigeria",
                        email: data.email || "info@yourcompany.com",
                        phone: data.phone_number || "+234 xxx xxx xxxx"
                    });
                }
            } catch (error) {
                console.error('Failed to fetch company info', error);
            }
        };
        fetchCompanyInfo();
    }, []);

    // Pagination calculations
    const totalRecords = ledgerData.length;
    const totalPages = Math.ceil(totalRecords / recordsPerPage);
    const startIndex = (currentPage - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;
    
    // Calculate running balance for all data first, then slice for pagination
    const dataWithRunningBalance = ledgerData.map((row, index) => {
        // Calculate running balance based on all previous transactions
        const currentBalance = ledgerData.slice(0, index + 1).reduce((balance, transaction) => {
            const debitAmount = transaction.rawDebit || parseFloat(transaction.debit?.replace(/[^\d.-]/g, '') || 0);
            const creditAmount = transaction.rawCredit || parseFloat(transaction.credit?.replace(/[^\d.-]/g, '') || 0);
            return balance + debitAmount - creditAmount;
        }, 0);

        return {
            ...row,
            runningBalance: formatMoney(Math.abs(currentBalance)),
            runningBalanceRaw: currentBalance
        };
    });

    // Get all data (no pagination)
    const currentPageData = dataWithRunningBalance;

    const stripCents = (value) => {
        if (value === null || value === undefined) return "";
        const str = String(value);
        const [intPart] = str.split(".");
        return intPart || "0";
    };

    const ledgerPeriodLabel = (() => {
        const year = new Date().getFullYear();
        return `Jan ${year} - Dec ${year}`;
    })();

    const openingBalance = currentPageData?.length ? stripCents(currentPageData[0].runningBalance) : "0";
    const closingBalance = currentPageData?.length
        ? stripCents(currentPageData[currentPageData.length - 1].runningBalance)
        : "0";

    const supplierAddress =
        customerDetails?.address ||
        customerDetails?.company_address ||
        customerDetails?.location ||
        "Lagos, Nigeria";

    const supplierPhone =
        customerDetails?.phone ||
        customerDetails?.phone_number ||
        customerDetails?.mobile ||
        "+234 xxx xxx xxxx";

    // Pagination handlers
    const handleFirstPage = () => setCurrentPage(1);
    const handleLastPage = () => setCurrentPage(totalPages);
    const handlePrevPage = () => setCurrentPage(prev => Math.max(1, prev - 1));
    const handleNextPage = () => setCurrentPage(prev => Math.min(totalPages, prev + 1));
    const handlePageClick = (pageNumber) => setCurrentPage(pageNumber);

    const [selectedRows, setSelectedRows] = useState([]);
    const allSelected = selectedRows.length === currentPageData.length;

    const handleSelectAll = () => {
        if (allSelected) {
            setSelectedRows([]);
        } else {
            setSelectedRows(currentPageData.map((_, i) => i));
        }
    };

    const handleSelectRow = (index) => {
        if (selectedRows.includes(index)) {
            setSelectedRows(selectedRows.filter(i => i !== index));
        } else {
            setSelectedRows([...selectedRows, index]);
        }
    };

    const pageMap = {
        '/dashboard': 'Dashboard',
        '/transactions': 'Transactions',
        '/accounts': 'Accounting',
        '/reports': 'Reports',
        '/banking': 'Banking',
        '/settings': 'Settings',
    };

    const handlePrint = () => {
        window.print();
    };

    const generatePDFBlob = () => {
        // Get the print area element using ref
        const element = printAreaRef.current;
        
        if (!element) {
            return null;
        }

        // Store original styles
        const originalBorder = element.style.border;
        const originalPadding = element.style.padding;
        const originalWidth = element.style.width;
        const iconBar = element.querySelector(`.${classes.iconbarsspg}`);
        const iconBarDisplay = iconBar ? iconBar.style.display : '';
        
        // Remove border and padding for PDF, set to 90% width, and hide the icon bar
        element.style.border = 'none';
        element.style.padding = '0';
        element.style.width = '90%';
        element.style.margin = '0 auto';
        if (iconBar) {
            iconBar.style.display = 'none';
        }

        // Define PDF options with minimal margins
        const options = {
            margin: 0,
            filename: `Receipt_${customerName}_${new Date().toISOString().split('T')[0]}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2 },
            jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4' }
        };

        // Return a promise that resolves with the PDF blob
        return new Promise((resolve) => {
            html2pdf().set(options).from(element).outputPdf('blob').then((blob) => {
                // Restore original styles after PDF is generated
                element.style.border = originalBorder;
                element.style.padding = originalPadding;
                element.style.width = originalWidth;
                element.style.margin = '';
                if (iconBar) {
                    iconBar.style.display = iconBarDisplay;
                }
                resolve(blob);
            });
        });
    };

    const generatePDF = async () => {
        const blob = await generatePDFBlob();
        if (!blob) {
            alert("Unable to generate receipt. Please try again.");
            return;
        }

        // Create a temporary download link for the blob
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Receipt_${customerName}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleDownload = async () => {
        await generatePDF();
    };

    const handleEmail = async () => {
        try {
            // Get customer email from customerDetails or ask user
            let recipientEmail = customerDetails?.email || '';
            
            if (!recipientEmail) {
                // Prompt user for email
                const { value: email } = await Swal.fire({
                    title: 'Send Receipt by Email',
                    input: 'email',
                    inputLabel: 'Recipient Email Address',
                    inputPlaceholder: 'Enter email address',
                    showCancelButton: true,
                    confirmButtonText: 'Send',
                    inputValidator: (value) => {
                        if (!value) {
                            return 'Email is required!';
                        }
                        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                            return 'Please enter a valid email address!';
                        }
                        return null;
                    }
                });
                
                if (!email) return; // User cancelled
                recipientEmail = email;
            }

            // Show loading message
            Swal.fire({
                title: 'Sending Receipt',
                text: 'Please wait while we prepare your email...',
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            // Generate PDF blob
            const pdfBlob = await generatePDFBlob();
            if (!pdfBlob) {
                Swal.fire('Error', 'Unable to generate receipt. Please try again.', 'error');
                return;
            }

            // Create FormData to send PDF to backend
            const formData = new FormData();
            formData.append('file', pdfBlob, `Receipt_${customerName}_${new Date().toISOString().split('T')[0]}.pdf`);
            formData.append('to', recipientEmail);
            formData.append('subject', `Employee Personal Ledger - ${customerName}`);
            formData.append('body', `Dear Employee,\n\nPlease find attached the Personal Ledger for ${customerName} as of ${new Date().toLocaleDateString()}.\n\nBest regards,\nPromix Accounting`);
            formData.append('customer_name', customerName);

            // Send email with PDF attachment via backend
            const response = await api.post('/v1/email/send-receipt', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success || response.status === 200) {
                Swal.fire('Success', 'Receipt has been sent to ' + recipientEmail, 'success');
            } else {
                Swal.fire('Error', 'Failed to send receipt. Please try again.', 'error');
            }
        } catch (error) {
            console.error('Error sending email:', error);
            Swal.fire('Error', error.response?.data?.message || 'Failed to send receipt. Please try again.', 'error');
        }
    };

    return (
        <div>
            <HeaderNav activePage={pageMap[location.pathname]} />

            <div className={classes.Container}>
                {/* Action Buttons */}
                <div className={classes.actions}>
                    <button className={classes.btns} onClick={() => navigate("/transactions/receivables/sales")}>
                        New Sales Invoice
                    </button>
                    <button className={classes.btns} onClick={() => navigate("/inventory/purchaseOrder")}>
                        New Purchase Invoice
                    </button>
                    <button className={classes.btns} onClick={() => navigate("/customer-sales")}>
                        All Invoices
                    </button>
                    <button className={`${classes.btns} ${classes.greens}`} onClick={() => navigate("/settings/product-services")}>
                        Product & Services
                    </button>
                </div>

                {/* Form and Icons */}
                <div className={classes.act}>
                    <form className={classes.form}>
                        <div className={classes.group}>
                            <label>Period:</label>
                            <div className={classes.dropdownWrapper}>
                                <select className={classes.statusDropdown}>
                                    <option>Current month</option>
                                    <option>Last month</option>
                                    <option>This year</option>
                                    <option>Custom range</option>
                                </select>
                                <span className={classes.arrowDown}>▼</span>
                            </div>
                        </div>

                        <div className={classes.group1}>
                            <label>Group by:</label>
                            <div className={classes.dropdownWrapper}>
                                <select className={classes.statusDropdown}>
                                    <option>None</option>
                                    <option>Date</option>
                                    <option>Transaction Type</option>
                                    <option>Amount</option>
                                </select>
                                <span className={classes.arrowDown}>▼</span>
                            </div>
                        </div>
                        <div className={classes.group1}>
                            <label>Start Date:</label>
                            <div className={classes.dropdownWrapper}>
                                <select className={classes.statusDropdown}>
                                    <option>01/01/25</option>
                                    <option>01/12/24</option>
                                    <option>01/11/24</option>
                                    <option>01/10/24</option>
                                </select>
                                <span className={classes.arrowDown}>▼</span>
                            </div>
                        </div>
                        <div className={classes.group1}>
                            <label>End Date:</label>
                            <div className={classes.dropdownWrapper}>
                                <select className={classes.statusDropdown}>
                                    <option>{new Date().toLocaleDateString('en-GB')}</option>
                                    <option>31/12/24</option>
                                    <option>30/11/24</option>
                                    <option>31/10/24</option>
                                </select>
                                <span className={classes.arrowDown}>▼</span>
                            </div>
                        </div>

                        <div className={classes.group1}>
                            <button className={classes.greenBtn} type="button">
                                <BarChart2 size={16} style={{ marginRight: "6px" }} />
                                Run Report
                            </button>
                        </div>
                    </form>

                    <div className={classes.iconbars}>
                        <div className={classes.iconbarr}>
                            <div className={classes.iconbar}>
                                <Filter size={16} style={{ marginRight: "6px" }} />
                                Filter
                            </div>
                            <div className={classes.iconbar}>
                                <CalendarClock size={16} style={{ marginRight: "6px" }} />
                                Schedule
                            </div>
                        </div>

                        <div className={classes.iconbarss}>
                            <div className={classes.iconbar}>
                                <Mail size={16} style={{ cursor: 'pointer' }} onClick={handleEmail} />
                            </div>
                            <div className={classes.iconbar}>
                                <Printer size={16} style={{ cursor: 'pointer' }} onClick={handlePrint} />
                            </div>
                            <div className={classes.iconbar}>
                                <Download size={16} style={{ cursor: 'pointer' }} onClick={handleDownload} />
                            </div>
                            <div className={classes.iconbar}>
                                <Settings size={16} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Back Button */}
                <div className={classes.matron}>
                    <div>
                        <button
                            className={classes.backButton}
                            onClick={() => navigate(-1)}
                        >
                            <ArrowLeft size={16} style={{ marginRight: '6px' }} />
                        </button>
                    </div>

                    <div>
                        <p className={classes.toplea}>Employee Personal Ledger</p>
                        <p className={classes.topl}>Financial position as of {new Date().toLocaleDateString()}</p>
                    </div>
                </div>

                {/* Report Content */}
                <div ref={printAreaRef} className={`${classes.containere} ${classes.printArea}`} style={{ marginTop: '40px' }}>
                <div className={classes.iconbarsspg}>
                            <div className={classes.iconbar}>
                                <Mail size={16} style={{ cursor: 'pointer' }} onClick={handleEmail} />
                            </div>
                            <div className={classes.iconbar}>
                                <Printer size={16} style={{ cursor: 'pointer' }} onClick={handlePrint} />
                            </div>
                            <div className={classes.iconbar}>
                                <Download size={16} style={{ cursor: 'pointer' }} onClick={handleDownload} />
                            </div>
                            <div className={classes.iconbar}>
                                <Settings size={16} />
                            </div>
                        </div>
                    <header className={classes.headers}>
                        <h1 className={classes.companyName}>Promix Accounting Ltd</h1>
                        <h1 className={classes.ledgerHeadingPrint}>EMPLOYEE PERSONAL LEDGER</h1>

                        <h2 className={classes.title}>EMPLOYEE PERSONAL LEDGER</h2>

                        <div className={classes.companyInfo}>
                            <strong>{companyInfo.name}</strong><br />
                            {companyInfo.address}<br />
                            Email: {companyInfo.email} | Phone: {companyInfo.phone}
                        </div>

                        <p className={classes.period}>For the period ended {new Date().toLocaleDateString()}</p>
                        <p className={classes.toplea}>{customerName}'s Personal Ledger</p>
                        <button className={classes.unaudited}>unaudited</button>
                    </header>

                    <div className={classes.supplierInfo}>
                        <div>
                            <strong>Employee Name:</strong> {customerName}<br />
                            <strong>Address:</strong> {supplierAddress}<br />
                            <strong>Phone:</strong> {supplierPhone}
                        </div>
                        <div>
                            <strong>Ledger Period:</strong> {ledgerPeriodLabel}<br />
                            <strong>Opening Balance:</strong> ₦{openingBalance}
                        </div>
                    </div>

                    <div className={classes.divider}></div>

                    {/* Table Container */}
                    <div className={classes.tableContainer}>
                        <table className={classes.table}>
                            <thead>
                                <tr className={classes.tableHeader}>
                                    <th>Date</th>
                                    <th>Description</th>
                                    <th>Ref No</th>
                                    <th style={{ textAlign: "right"}}>Debit (₦)</th>
                                    <th style={{ textAlign: "right" }}>Credit (₦)</th>
                                    <th style={{ textAlign: "right" }}>Balance (₦)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentPageData.length > 0 ? (
                                    currentPageData.map((row, index) => (
                                        <tr key={row.id || index} className={classes.tableRow}>
                                            <td className={classes.tableCell}>{row.date}</td>
                                            <td
                                                className={classes.tableCell}
                                                title={row.description || ""}
                                            >
                                                {row.description}
                                            </td>
                                            <td className={classes.tableCell}>{row.ref}</td>
                                            <td style={{ textAlign: "right",  color: 'red'  }}>
                                                {row.debit ? stripCents(row.debit) : "-"}
                                            </td>
                                            <td style={{ textAlign: "right" }}>
                                                {row.credit ? stripCents(row.credit) : "-"}
                                            </td>
                                            <td style={{ textAlign: "right" }}>{stripCents(row.runningBalance)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                                            No transactions found for {customerName}
                                        </td>
                                    </tr>
                                )}
                                {/* No empty row padding when showing all data */}
                            </tbody>
                        </table>
                    </div>

                    <div className={classes.footer}>
                        Closing Balance: ₦{closingBalance}
                    </div>

                    <div className={classes.generatedByFooter}>
                        <div>Generated by</div>
                        <div>Promix Accounting System</div>
                        <div>www.promixaccounting.com</div>
                    </div>

                    {/* Pagination */}
                    {/* {totalPages > 1 && (
                        <div className={classes.paginationContainer} style={{ marginTop: '20px', marginBottom: '20px' }}>
                            <table className={classes.footerTable}>
                                <tbody>
                                    <tr>
                                        <td 
                                            className={classes.frston}
                                            onClick={handleFirstPage}
                                            style={{ 
                                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                                opacity: currentPage === 1 ? 0.5 : 1 
                                            }}
                                        >
                                            First
                                        </td>
                                        <td 
                                            className={classes.twono}
                                            onClick={handlePrevPage}
                                            style={{ 
                                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                                opacity: currentPage === 1 ? 0.5 : 1 
                                            }}
                                        >
                                            Previous
                                        </td>
                                        <td className={`${classes.page} ${classes.activePage}`}>
                                            {currentPage}
                                        </td>
                                        <td 
                                            className={classes.twono}
                                            onClick={handleNextPage}
                                            style={{ 
                                                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                                opacity: currentPage === totalPages ? 0.5 : 1 
                                            }}
                                        >
                                            Next
                                        </td>
                                        <td 
                                            className={classes.lastno}
                                            onClick={handleLastPage}
                                            style={{ 
                                                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                                opacity: currentPage === totalPages ? 0.5 : 1 
                                            }}
                                        >
                                            Last
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            <div className={classes.lasone}>
                                <div className={classes.paginationDisplay}>
                                    Displaying {startIndex + 1} - {Math.min(endIndex, totalRecords)} of {totalRecords}
                                </div>
                            </div>
                        </div>
                    )} */}
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default PersonalLedger;